import React, { useState, useEffect } from "react";
import styles from "./PounceConnectorsModal.module.css";
import { getPounceKeys, createPounceKey } from "../../../Store/apiStore";

// navigator.clipboard sirf secure context (https / localhost) me milta hai aur
// app ke WebView me aksar hota hi nahi — tab purana execCommand rasta.
const copyText = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* permission denied / insecure context — fallback below */
  }
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
};

export default function PounceConnectorsModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(true);
  const [keyData, setKeyData] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [copyFailedField, setCopyFailedField] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [activeTab, setActiveTab] = useState("make");

  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;
    const fetchKeys = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const keys = await getPounceKeys(true);
        if (mounted) {
          if (keys && keys.length > 0) {
            setKeyData(keys[0]);
          } else {
            // Auto create key if empty
            const res = await createPounceKey("Default Outbound Caller Webhook");
            if (res?.success) {
              setKeyData({
                webhookUrl: res.webhookUrl,
                apiKey: res.apiKey,
                publicRef: res.publicRef,
              });
            }
          }
        }
      } catch (err) {
        console.error("Error fetching Pounce keys:", err);
        if (mounted) setLoadError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchKeys();
    return () => {
      mounted = false;
    };
  }, [isOpen, reloadTick]);

  // Popup khula ho to peeche ka page scroll na ho; band hote hi pehle jaisa.
  // Sirf body ka overflow kaafi nahi tha (page phir bhi khisakta tha) — isliye
  // NewModal wala tareeka: <html> + <body> dono band, aur body apni jagah pin.
  useEffect(() => {
    if (!isOpen) return undefined;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const { body } = document;
    const html = document.documentElement;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
    };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopy = async (text, fieldName) => {
    if (!text) return;
    const ok = await copyText(text);
    setCopiedField(ok ? fieldName : null);
    setCopyFailedField(ok ? null : fieldName);
    setTimeout(() => {
      setCopiedField(null);
      setCopyFailedField(null);
    }, 2000);
  };
  const copyLabel = (fieldName, idle) =>
    copiedField === fieldName
      ? "Copied!"
      : copyFailedField === fieldName
        ? "Copy failed"
        : idle;

  // Purani key ka poora secret server pe sirf hash me hai (encryption key ke
  // bina bani thi) — wo dobara dikh nahi sakti. Nayi key ka secret milta hai.
  const handleGenerateKey = async () => {
    setGenerating(true);
    try {
      const res = await createPounceKey("Default Outbound Caller Webhook");
      if (res?.success) {
        setKeyData({
          webhookUrl: res.webhookUrl,
          apiKey: res.apiKey,
          publicRef: res.publicRef,
        });
        setShowKey(true);
      }
    } catch (err) {
      console.error("Error creating Pounce key:", err);
    } finally {
      setGenerating(false);
    }
  };

  const samplePayload = JSON.stringify(
    {
      phone: "+1 (555) 234-5678",
      name: "John Doe",
      email: "john@example.com",
      inquiry: "Need emergency repair quote",
      source: "Make.com Webhook",
    },
    null,
    2
  );

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTitleWrap}>
            <div className={styles.iconBadge}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path
                  d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <h2 className={styles.title}>Outbound Caller Webhook & Connectors</h2>
              <p className={styles.subtitle}>
                Send new leads from Make.com, Zapier, Webflow or your CRM, and Outbound Caller calls them within seconds.
              </p>
            </div>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {loading ? (
            // Asli layout ke size ka shimmer — data aate hi popup uchhalta nahi.
            <div className={styles.skeleton} aria-busy="true" aria-label="Loading webhook details">
              <span className={`${styles.shimmer} ${styles.skLabel}`} />
              <span className={`${styles.shimmer} ${styles.skInput}`} />
              <span className={`${styles.shimmer} ${styles.skLabel}`} />
              <span className={`${styles.shimmer} ${styles.skInput}`} />
              <span className={`${styles.shimmer} ${styles.skNote}`} />
              <span className={`${styles.shimmer} ${styles.skTabs}`} />
              {[92, 78, 60, 96, 84, 70].map((w) => (
                <span key={w} className={`${styles.shimmer} ${styles.skLine}`} style={{ width: `${w}%` }} />
              ))}
            </div>
          ) : loadError || !keyData ? (
            <div className={styles.loadingState}>
              <span>We couldn&apos;t load your webhook details.</span>
              <button
                type="button"
                className={styles.inputBtn}
                onClick={() => setReloadTick((t) => t + 1)}
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              {/* Webhook URL */}
              <div className={styles.section}>
                <div className={styles.labelRow}>
                  <span className={styles.label}>Webhook Endpoint (POST)</span>
                  <span className={styles.tag}>Live</span>
                </div>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    readOnly
                    value={keyData.webhookUrl || ""}
                  />
                  <button
                    type="button"
                    className={`${styles.inputBtn} ${
                      copiedField === "url" ? styles.inputBtnCopied : ""
                    }`}
                    onClick={() => handleCopy(keyData?.webhookUrl, "url")}
                  >
                    {copyLabel("url", "Copy URL")}
                  </button>
                </div>
              </div>

              {/* API Key */}
              <div className={styles.section}>
                <div className={styles.labelRow}>
                  <span className={styles.label}>API Secret Key</span>
                  <span className={styles.tag}>Header: X-Outbound-Key</span>
                </div>
                <div className={styles.inputGroup}>
                  <input
                    type={keyData.apiKey && !showKey ? "password" : "text"}
                    readOnly
                    value={keyData.apiKey || `${keyData.keyPrefix || "pk_live_"}…`}
                  />
                  {keyData.apiKey ? (
                    <>
                      <button
                        type="button"
                        className={styles.inputBtn}
                        onClick={() => setShowKey(!showKey)}
                      >
                        {showKey ? "Hide" : "View"}
                      </button>
                      <button
                        type="button"
                        className={`${styles.inputBtn} ${
                          copiedField === "key" ? styles.inputBtnCopied : ""
                        }`}
                        onClick={() => handleCopy(keyData.apiKey, "key")}
                      >
                        {copyLabel("key", "Copy Key")}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className={styles.inputBtn}
                      onClick={handleGenerateKey}
                      disabled={generating}
                    >
                      {generating ? "Generating…" : "Generate new key"}
                    </button>
                  )}
                </div>
                {!keyData.apiKey && (
                  <p className={styles.hint}>
                    This key&apos;s full secret can&apos;t be shown again. Generate a new key to
                    view and copy it — your webhook URL will change, so update it wherever you
                    send leads from.
                  </p>
                )}
              </div>

              {/* Header Info Note */}
              <div className={styles.headerInfoBox}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <div>
                  Every request must include your API Secret Key in the{" "}
                  <span className={styles.codeBadge}>X-Outbound-Key</span> header. Requests without it are rejected.
                </div>
              </div>

              {/* Instructions / Payload Tabs */}
              <div className={styles.section}>
                <div className={styles.tabsRow}>
                  <button
                    type="button"
                    className={`${styles.tabBtn} ${activeTab === "make" ? styles.tabBtnActive : ""}`}
                    onClick={() => setActiveTab("make")}
                  >
                    Make.com Setup
                  </button>
                  <button
                    type="button"
                    className={`${styles.tabBtn} ${activeTab === "zapier" ? styles.tabBtnActive : ""}`}
                    onClick={() => setActiveTab("zapier")}
                  >
                    Zapier Setup
                  </button>
                  <button
                    type="button"
                    className={`${styles.tabBtn} ${activeTab === "payload" ? styles.tabBtnActive : ""}`}
                    onClick={() => setActiveTab("payload")}
                  >
                    JSON Payload
                  </button>
                </div>

                {/* Field ke naam Make.com ke HTTP "Make a request" module wale hi hain. */}
                {activeTab === "make" && (
                  <ol className={styles.stepsList}>
                    <li>In your Make.com scenario, add the <strong>HTTP &gt; Make a request</strong> module.</li>
                    <li>Paste the <strong>Webhook Endpoint</strong> shown above into <strong>URL</strong>.</li>
                    <li>Set <strong>Method</strong> to <span className={styles.codeBadge}>POST</span>.</li>
                    <li>
                      Add your API key. Set <strong>Authentication type</strong> to <strong>API key</strong> and create a
                      credential: paste the <strong>API Secret Key</strong> shown above as the key, name it{" "}
                      <span className={styles.codeBadge}>X-Outbound-Key</span>, and send it in the header.
                    </li>
                    <li>
                      Prefer not to use a credential? Leave authentication off and add the key under{" "}
                      <strong>Headers</strong> instead: name <span className={styles.codeBadge}>X-Outbound-Key</span>, value
                      your API Secret Key.
                    </li>
                    <li>
                      Set <strong>Body content type</strong> to <strong>application/JSON</strong> and{" "}
                      <strong>Body input method</strong> to <strong>JSON string</strong>, then paste the JSON from the{" "}
                      <strong>JSON Payload</strong> tab and map your lead&apos;s phone, name, email and inquiry.
                    </li>
                    <li>Run the scenario once. The lead should appear in Outbound Caller within a few seconds.</li>
                  </ol>
                )}

                {activeTab === "zapier" && (
                  <ol className={styles.stepsList}>
                    <li>In your Zap, add the <strong>Webhooks by Zapier &gt; POST</strong> action.</li>
                    <li>Paste the <strong>Webhook Endpoint</strong> shown above into <strong>URL</strong>.</li>
                    <li>Set <strong>Payload Type</strong> to <span className={styles.codeBadge}>json</span>.</li>
                    <li>
                      Add your API key under <strong>Headers</strong>: key{" "}
                      <span className={styles.codeBadge}>X-Outbound-Key</span>, value your API Secret Key.
                    </li>
                    <li>
                      Under <strong>Data</strong>, map <span className={styles.codeBadge}>phone</span>,{" "}
                      <span className={styles.codeBadge}>name</span>, <span className={styles.codeBadge}>email</span> and{" "}
                      <span className={styles.codeBadge}>inquiry</span> from your lead.
                    </li>
                    <li>Test the Zap. The lead should appear in Outbound Caller within a few seconds.</li>
                  </ol>
                )}

                {activeTab === "payload" && (
                  <div className={styles.codeBoxWrap}>
                    <pre className={styles.codeBox}>{samplePayload}</pre>
                    <button
                      type="button"
                      className={styles.copyCodeBtn}
                      onClick={() => handleCopy(samplePayload, "payload")}
                    >
                      {copyLabel("payload", "Copy JSON")}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button type="button" className={styles.doneBtn} onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
